import logger from "src/utils/logger";

class ConditionEvaluator {
    evaluateConditions(conditions, context) {
        throw new Error('evaluateConditions method must be implemented');
    }
}

class ClientFormConditionEvaluator extends ConditionEvaluator {
    static #_current = null;
    static current() {
        if (ClientFormConditionEvaluator.#_current === null) {
            ClientFormConditionEvaluator.#_current = new ClientFormConditionEvaluator();
        }
        return ClientFormConditionEvaluator.#_current;
    }
    evaluateConditions(conditions, form) {
        if (!conditions.length) {
            return true;
        }

        return conditions.reduce((accumulator, currentCondition, index) => {
            let field = form.find(item =>
                item.id === currentCondition.field.id ||
                (item.config.type === "section" && item.fields.some(subItem => subItem.id === currentCondition.field.id))
            );

            if (field && field.config && field.config.type === "section") {
                field = field.fields.find(subItem => subItem.id === currentCondition.field.id);
            }

            let fieldValue = null;
            if (field) {
                fieldValue = field.value;

                if (field.config.type === "section" && field.fields.length > 0) {
                    fieldValue = field.fields[0].value;
                }
            }

            const conditionResult = field
                ? this.checkCondition(currentCondition.condition, fieldValue, currentCondition.value)
                : false;

            if (index === 0) {
                return conditionResult;
            } else {
                if (currentCondition.relation === "AND") {
                    return accumulator && conditionResult;
                } else if (currentCondition.relation === "OR") {
                    return accumulator || conditionResult;
                }
            }
        }, false);
    }

    checkCondition(condition, value, targetValue) {
        logger.log(value);
        logger.log(condition);
        logger.log(targetValue);

        switch (condition) {
            case "is":
                return value === targetValue;
            case "is not":
                return value !== targetValue;
            case "contains":
                return value?.includes(targetValue);
            case "does not contain":
                return !value?.includes(targetValue);
            case "begins with":
                return value?.startsWith(targetValue);
            case "ends with":
                return value?.endsWith(targetValue);
            case "is equal to":
                return value === targetValue;
            case "is greater than":
                return parseFloat(value) > parseFloat(targetValue);
            case "is less than":
                return parseFloat(value) < parseFloat(targetValue);
            case "is on":
                return new Date(value).toDateString() === new Date(targetValue).toDateString();
            case "is not on":
                return new Date(value).toDateString() !== new Date(targetValue).toDateString();
            case "is before":
                return new Date(value) < new Date(targetValue);
            case "is after":
                return new Date(value) > new Date(targetValue);
            case "is uploaded":
                return value !== null && value !== undefined && value !== "";
            case "is empty":
                return value === null || value === undefined || value === "";
            case "include":
                return Array.isArray(value) && value.includes(targetValue);
            case "exclude":
                return Array.isArray(value) && !value.includes(targetValue);
            default:
                return false;
        }
    }
}

class FlowConditionEvaluator extends ConditionEvaluator {
    static #_current = null;

    constructor() {
        super();
        this.operatorMap = {
            "eq": "===",
            "gt": ">",
            "lt": "<",
            "gte": ">=",
            "lte": "<=",
            "neq": "!==",
            "in": "includes",
            "nin": "notIncludes"
        };
    }

    static current() {
        if (FlowConditionEvaluator.#_current === null) {
            FlowConditionEvaluator.#_current = new FlowConditionEvaluator();
        }
        return FlowConditionEvaluator.#_current;
    }

    evaluateConditions(rules, context) {
        for (const rule of rules) {
            if (rule.conditionType === "rule") {
                logger.log("Evaluating rule with edgeId:", rule.edgeId);
                logger.log("Conditions:", rule.conditions);

                const result = rule.conditions.some(conditionGroup =>
                    conditionGroup.every(({ selectedField, condition, value, inputType }) => {
                        let contextValue = this.extractValue(selectedField, context);

                        logger.log(`Selected Field: ${selectedField}`);
                        logger.log(`Condition: ${condition}`);
                        logger.log(`Expected Value: ${value}`);
                        logger.log(`Actual Context Value: ${contextValue}`);
                        if (contextValue === null || contextValue === undefined) {
                            return false
                        }
                        if (!inputType) {
                            if (typeof contextValue === 'object' && contextValue instanceof Date) {
                                inputType = 'date';
                            } else if (typeof contextValue === 'string') {
                                const parsedDate = Date.parse(contextValue);
                                if (parsedDate) {
                                    inputType = 'date';
                                } else {
                                    inputType = 'string';
                                }
                            } else if (typeof contextValue === 'number') {
                                inputType = 'number';
                            } else if (typeof contextValue === 'boolean') {
                                inputType = 'boolean';
                            } else {
                                inputType = 'string';
                            }
                        }



                        if (inputType === 'date' || inputType === 'datetime') {
                            contextValue = new Date(contextValue);
                            value = new Date(value);
                        } else if (inputType === 'number') {
                            contextValue = parseFloat(contextValue);
                            value = parseFloat(value);
                        } else if (inputType === 'boolean') {
                            contextValue = Boolean(contextValue);
                            value = Boolean(value);
                        } else if (inputType === 'string') {
                            contextValue = String(contextValue);
                            value = String(value);
                        }

                        const conditionResult = this.checkCondition(condition, contextValue, value, inputType);
                        logger.log(`Condition Result: ${conditionResult}`);
                        return conditionResult;
                    })
                );

                if (result) {
                    logger.log("Rule passed. Returning edgeId:", rule.edgeId);
                    return rule.edgeId;
                } else {
                    logger.log("Rule failed.");
                }
            }
        }

        const elseRule = rules.find(rule => rule.conditionType === "else");
        if (elseRule) {
            logger.log("No rule matched. Returning else edgeId:", elseRule.edgeId);
            return elseRule.edgeId;
        }

        throw new Error("No valid rule or else condition found.");
    }



    checkCondition(condition, value, targetValue, inputType) {
        if (inputType === 'date' || inputType === 'datetime') {
            value = value.getTime();
            targetValue = targetValue.getTime();
        }

        switch (condition) {
            case "eq":
                return value === targetValue;
            case "neq":
                return value !== targetValue;
            case "gt":
                return value > targetValue;
            case "lt":
                return value < targetValue;
            case "gte":
                return value >= targetValue;
            case "lte":
                return value <= targetValue;
            case "in":
                return (Array.isArray(value) && value.includes(targetValue)) ||
                    (typeof value === 'string' && value.includes(targetValue));
            case "nin":
                return (Array.isArray(value) && !value.includes(targetValue)) ||
                    (typeof value === 'string' && !value.includes(targetValue));
            default:
                return false;
        }
    }


    extractValue(selectedField, context) {
        const fieldPath = selectedField.replace('context.', '').split('.');
        return fieldPath.reduce((acc, key) => acc && acc[key], context);
    }
}



class ContextWithEvalConditionEvaluator extends ConditionEvaluator {
    static #_current = null;
    constructor() {
        super();
        this.operatorMap = {
            "eq": "===",
            "gt": ">",
            "lt": "<",
            "gte": ">=",
            "lte": "<=",
            "neq": "!==",
            "in": "includes",
            "nin": "notIncludes"
        };
    }
    static current() {
        if (ContextWithEvalConditionEvaluator.#_current === null) {
            ContextWithEvalConditionEvaluator.#_current = new ContextWithEvalConditionEvaluator();
        }
        return ContextWithEvalConditionEvaluator.#_current;
    }

    convertCondition(condition, context) {
        let convertedCondition = condition.map(orConditions => {
            let andConditions = orConditions.map(({ selectedField, condition, value, inputType }) => {
                let contextValue = this.replaceValueFromContext(selectedField, context);
                if (inputType === 'text') {
                    if (condition === 'in' || condition === 'nin') {
                        value = `[${value.split(',').map(val => `'${val.trim()}'`).join(', ')}]`;
                        if (condition === 'nin') {
                            return `!${contextValue}.includes(${value})`;
                        }
                        return `${contextValue}.includes(${value})`;
                    } else {
                        value = `'${value}'`;
                    }
                } else if (inputType === 'date') {
                    contextValue = `new Date(${contextValue})`;
                    value = `new Date('${value}')`;
                }
                return `${contextValue} ${this.operatorMap[condition]} ${value}`;
            });
            return `(${andConditions.join(' && ')})`;
        });

        return convertedCondition.join(' || ');
    }

    replaceValueFromContext(item, context) {
        const regxp = /(context\.\w+((\.\w+)*))/;
        let match;
        while ((match = regxp.exec(item)) !== null) {
            let context_var = match[0];
            let context_val = context_var.split('.');
            context_val.shift();
            let mapped = context;

            try {
                context_val.forEach(context_part => {
                    if (mapped[context_part] !== undefined) {
                        mapped = mapped[context_part];
                    } else {
                        throw new Error(`Property ${context_part} does not exist on context`);
                    }
                });
            } catch (error) {
                logger.error(error.message);
            }

            if (typeof mapped === 'string') {
                mapped = `'${mapped}'`;
            } else if (mapped instanceof Date) {
                mapped = `new Date('${mapped.toISOString()}')`;
            }

            item = item.replace(context_var, mapped);
        }
        return item;
    }

    evaluateConditions(conditions, context) {
        const finalCondition = this.convertCondition(conditions, context);

        try {
            return eval(finalCondition);
        } catch (error) {
            logger.error(`Failed to evaluate condition: ${finalCondition}`, error);
            return false;
        }
    }
}

export { ClientFormConditionEvaluator, ContextWithEvalConditionEvaluator, FlowConditionEvaluator };
